import type { CmmdBoardTask, CmmdProjectMilestone } from "@cmmd-forge/contracts";

import { getStatusConfig } from "~/components/command-center/statusConfig";
import type { ViewsFilterItem } from "~/components/data-views/ViewsAndFiltersPanel";
import { taskStatusDescriptor } from "~/components/projects/boardColumns";
import { formatWorkItemId } from "~/lib/formatWorkItemId";
import type { DataViewState } from "~/views/viewDefinition";
import { activeTasksInOrder, normalizeTaskStatus } from "./taskViewTypes";
import { taskAssigneeName } from "./taskPresentation";

export const UNASSIGNED_OWNER_FILTER = "__unassigned__";
export const NO_MILESTONE_FILTER = "__no_milestone__";

export function buildTaskStatusFilterItems(tasks: ReadonlyArray<CmmdBoardTask>): {
  readonly items: ReadonlyArray<ViewsFilterItem>;
  readonly totalCount: number;
} {
  const active = activeTasksInOrder(tasks);
  const counts = new Map<string, number>();
  for (const task of active) {
    const status = taskStatusDescriptor(task).key;
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }

  const items: ViewsFilterItem[] = [
    { value: "", label: "All statuses", count: active.length },
    ...[...counts.entries()]
      .toSorted(([left], [right]) => left.localeCompare(right))
      .map(([status, count]) => {
        const config = getStatusConfig(status);
        return {
          value: status,
          label: config.label,
          count,
          visual: { kind: "dot" as const, className: config.dotColor },
        };
      }),
  ];

  return { items, totalCount: active.length };
}

export function buildTaskAssigneeFilterItems(
  tasks: ReadonlyArray<CmmdBoardTask>,
): ReadonlyArray<ViewsFilterItem> {
  const active = activeTasksInOrder(tasks);
  const byName = new Map<string, { count: number; avatarUrl: string | null }>();
  let unassigned = 0;

  for (const task of active) {
    const name = taskAssigneeName(task)?.trim() ?? "";
    if (!name) {
      unassigned += 1;
      continue;
    }
    const current = byName.get(name);
    byName.set(name, {
      count: (current?.count ?? 0) + 1,
      avatarUrl: task.assigneeAvatar ?? current?.avatarUrl ?? null,
    });
  }

  return [
    { value: "", label: "All assignees", count: active.length },
    { value: UNASSIGNED_OWNER_FILTER, label: "Unassigned", count: unassigned },
    ...[...byName.entries()]
      .toSorted(([left], [right]) => left.localeCompare(right, undefined, { sensitivity: "base" }))
      .map(([name, entry]) => ({
        value: name,
        label: name,
        count: entry.count,
        visual: {
          kind: "owner" as const,
          avatarUrl: entry.avatarUrl,
          label: name,
        },
      })),
  ];
}

export function buildTaskMilestoneFilterItems(
  tasks: ReadonlyArray<CmmdBoardTask>,
  milestones: ReadonlyArray<CmmdProjectMilestone>,
): ReadonlyArray<ViewsFilterItem> {
  const active = activeTasksInOrder(tasks);
  const counts = new Map<number, number>();
  let withoutMilestone = 0;
  for (const task of active) {
    if (task.milestoneId === null) {
      withoutMilestone += 1;
    } else {
      counts.set(task.milestoneId, (counts.get(task.milestoneId) ?? 0) + 1);
    }
  }
  return [
    { value: "", label: "All milestones", count: active.length },
    { value: NO_MILESTONE_FILTER, label: "No milestone", count: withoutMilestone },
    ...milestones.map((milestone) => ({
      value: String(milestone.id),
      label: milestone.title,
      count: counts.get(milestone.id) ?? 0,
    })),
  ];
}

function taskMatchesSearch(
  task: CmmdBoardTask,
  query: string,
  workItemPrefix: string | null | undefined,
): boolean {
  if (query.length === 0) return true;
  if (task.title.toLowerCase().includes(query)) return true;

  const id = String(task.id);
  const canonicalId = formatWorkItemId(workItemPrefix, task.id).toLowerCase();
  return query === id || query === `#${id}` || query === canonicalId;
}

function taskMatchesStatus(task: CmmdBoardTask, statusFilter: string): boolean {
  if (!statusFilter) return true;
  return normalizeTaskStatus(taskStatusDescriptor(task).key) === statusFilter;
}

function taskMatchesOwner(task: CmmdBoardTask, ownerFilter: string): boolean {
  if (!ownerFilter) return true;
  const assignee = taskAssigneeName(task)?.trim() ?? "";
  if (ownerFilter === UNASSIGNED_OWNER_FILTER) return assignee.length === 0;
  return assignee === ownerFilter;
}

function taskMatchesMilestone(task: CmmdBoardTask, milestoneFilter: string): boolean {
  if (!milestoneFilter) return true;
  if (milestoneFilter === NO_MILESTONE_FILTER) return task.milestoneId === null;
  return task.milestoneId === Number(milestoneFilter);
}

/** Root-task filter used by the project Tasks surface (search + status + assignee). */
export function filterProjectTasks(
  tasks: ReadonlyArray<CmmdBoardTask>,
  state: Pick<DataViewState, "search" | "statusFilter" | "ownerFilter" | "milestoneFilter">,
  workItemPrefix?: string | null,
): readonly CmmdBoardTask[] {
  const query = state.search.trim().toLowerCase();
  return tasks.filter(
    (task) =>
      taskMatchesSearch(task, query, workItemPrefix) &&
      taskMatchesStatus(task, state.statusFilter) &&
      taskMatchesOwner(task, state.ownerFilter) &&
      taskMatchesMilestone(task, state.milestoneFilter),
  );
}
